// semeia-disable-reexport
import type BioExamOrder from "parog-web/models/bio-exam-order";
import type BioExamObservation from "parog-web/models/loinc";

type BioExam = BioExamObservation | BioExamOrder;
type ExamsByLoincChapters = Map<string, BioExam[]>;

export type ExamsByGroup = Map<string, ExamsByLoincChapters>;

const loincChapterOrder = [
  "Hématologie",
  "Biochimie",
  "Microbiologie",
  "Dosages de médicaments et substances"
];

/**
 * Returns bio exam observations or bio exam orders grouped by LOINC chapters,
 * themselves grouped by "specimen" group (blood, urinary or other)
 *
 * example:
 * {
 *    {"blood":
 *      {"Hématologie": [BioExamObservation#1, BioExamObservation#2]},
 *      {"Microbiologie": [BioExamObservation#3]}
 *    },
 *    {"urinary":
 *      {"Biochimie": [BioExamObservation#4]}
 *    }
 * }
 */
export function getGroupedBioExams(bioExams: BioExam[]): ExamsByGroup {
  const loincChapterBySpecimenGroup: ExamsByGroup = new Map();

  // Sort bio exams by LOINC chapter to make sure they always have the same order
  bioExams = bioExams.toSorted(
    (a, b) =>
      loincChapterOrder.findIndex(e => e == a.loincChapterLabel) -
      loincChapterOrder.findIndex(e => e == b.loincChapterLabel)
  );

  // Build "specimen" groups
  // specimen = blood | urinary | other
  const examsBySpecimenGroup: Record<string, BioExam[]> = {};

  if (bioExams.find(exam => exam.specimen == "BLD")) {
    examsBySpecimenGroup["blood"] = [];
  }

  if (bioExams.find(exam => exam.specimen == "UR")) {
    examsBySpecimenGroup["urinary"] = [];
  }

  if (
    bioExams.find(
      exam => !exam.specimen || !["BLD", "UR"].includes(exam.specimen)
    )
  ) {
    examsBySpecimenGroup["other"] = [];
  }

  // Fill specimen groups
  bioExams.forEach(exam => {
    switch (exam.specimen) {
      case "BLD":
        examsBySpecimenGroup.blood.push(exam);
        break;
      case "UR":
        examsBySpecimenGroup.urinary.push(exam);
        break;
      default:
        examsBySpecimenGroup.other.push(exam);
        break;
    }
  });

  // Add and fill LOINC chapter groups within specimen groups
  Object.keys(examsBySpecimenGroup).forEach(specimen => {
    const examsByLoincChapter = new Map<string, BioExam[]>();

    examsBySpecimenGroup[specimen].forEach(exam => {
      if (!examsByLoincChapter.has(exam.loincChapterLabel)) {
        examsByLoincChapter.set(exam.loincChapterLabel, []);
      }

      examsByLoincChapter.get(exam.loincChapterLabel)?.push(exam);
    });

    loincChapterBySpecimenGroup.set(specimen, examsByLoincChapter);
  });

  return loincChapterBySpecimenGroup;
}
